import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Section } from "@/components/Primitives";
import { LEGAL_PAGES, GENERAL_DISCLAIMER } from "@/data/content";
import NotFound from "@/pages/NotFound";

const reviewed = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

const CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    body: [
      ["Overview", "Global Pass Consultants respects your privacy. This policy explains how we collect, use and protect the information you provide through our website and forms."],
      ["Information We Collect", "We collect the information you submit through inquiry and assessment forms, such as your name, email, phone, country and business details, together with privacy-friendly campaign source data and the page you contacted us from."],
      ["How We Use It", "We use your information to respond to your inquiry, coordinate professional support and improve our services. We do not sell your personal information."],
      ["Data Handling", "Submissions are stored securely on our servers. A backup copy of each inquiry is retained internally for continuity. Access is restricted to authorised processes and designated staff."],
      ["Your Rights", "You may request access to, correction of, or deletion of your personal information. Contact our privacy officer to know more."],
      ["Contact", "For privacy questions, contact us to know more."],
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    body: [
      ["Acceptance", "By using this website you agree to these terms. If you do not agree, please do not use the site."],
      ["Nature of Services", "Global Pass Consultants provides business consulting, coordination and market-entry support. We are not a government authority, bank, law firm, tax practice or immigration regulator."],
      ["No Guarantee", "No outcome is guaranteed. Company formation, licensing, tax treatment, banking, work permits and immigration outcomes depend on your circumstances, applicable laws and decisions made by third parties or government authorities."],
      ["Information Only", "Website content is general information and does not constitute legal, tax or immigration advice."],
      ["Contact", "For questions about these terms, contact us to know more."],
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    body: [
      ["Use of Cookies", "We use privacy-friendly cookies to operate the website, remember your preferences and understand how the site is used."],
      ["Managing Cookies", "You can accept essential cookies only or all cookies via our cookie banner, and you can adjust your browser settings at any time."],
      ["Analytics Readiness", "The site is prepared for analytics tools which may be enabled with appropriate consent. Contact us to know more."],
    ],
  },
  "professional-disclaimer": {
    title: "Professional Disclaimer",
    body: [["General Disclaimer", GENERAL_DISCLAIMER],
      ["Regulated Services", "Regulated legal, tax, trust company and immigration services are provided by or referred to appropriately licensed professionals in the relevant jurisdiction."],
      ["Tax Treatment", "Tax treatment depends on activity, substance and compliance. Independent tax advice is required."]],
  },
  "refund-and-cancellation": {
    title: "Refund and Cancellation Policy",
    body: [
      ["Commercial Terms", "Commercial terms, including any refund or cancellation arrangements, depend on the specific engagement and services involved."],
      ["How to Discuss", "Where a refund or cancellation policy requires commercial details, please contact us to know more."],
    ],
  },
  "aml-due-diligence": {
    title: "AML and Client Due Diligence Notice",
    body: [
      ["Commitment", "We are committed to anti-money-laundering standards and client due diligence. We and our licensed partners may request identification, proof of address and source-of-funds information."],
      ["Cooperation", "Onboarding and coordination are subject to satisfactory due diligence. Certain activities may require additional checks. Contact us to know more."],
    ],
  },
  "document-handling": {
    title: "Document Handling Notice",
    body: [
      ["Secure Handling", "Documents you provide are handled securely. We do not send sensitive documents as ordinary email attachments; internal recipients receive protected notifications or secure links."],
      ["Access & Retention", "Access is restricted to authorised processes and designated staff. Secure retention and deletion controls apply."],
      ["Approved File Types", "Where uploads are enabled, only approved file types and size limits are accepted and files are scanned before storage."],
    ],
  },
  "complaints-policy": {
    title: "Complaints Policy",
    body: [
      ["Raising a Concern", "We take concerns seriously. If you are unhappy with any aspect of our service, please tell us so we can address it."],
      ["Our Process", "We will acknowledge your complaint, review it fairly and respond within a reasonable time. Contact us to know more."],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const page = CONTENT[slug];
  const meta = LEGAL_PAGES.find((p) => p.slug === slug);
  const otherPages = useMemo(() => LEGAL_PAGES.filter((p) => p.slug !== slug), [slug]);
  if (!page) return <NotFound />;

  return (
    <>
      <SEO title={page.title} description={`${page.title} — Global Pass Consultants. Contact us to know more.`} path={`/legal/${slug}`} />
      <div className="bg-navy text-white pt-32 pb-14 md:pt-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-wide text-gold">Legal</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold">{page.title}</h1>
          <p className="mt-3 text-sm text-slate-400">Last reviewed: {reviewed}</p>
        </div>
      </div>
      <Section className="bg-white">
        <div className="max-w-3xl mx-auto space-y-8">
          {page.body.map(([h, t]) => (
            <div key={h}>
              <h2 className="text-lg font-bold text-navy">{h}</h2>
              <p className="mt-2 text-slate-600 leading-relaxed">{t}</p>
            </div>
          ))}
          <div className="pt-6 border-t border-slate-100">
            <h3 className="font-bold text-navy mb-3">Other Legal Pages</h3>
            <div className="flex flex-wrap gap-2">
              {otherPages.map((p) => (
                <Link key={p.slug} to={`/legal/${p.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">{p.title}</Link>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
